import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Field } from "react-final-form";
import AuthService from "../../../services/AuthService";
import { mfaChallengeReceived, sessionSuccess } from "../../../store/slices/authSlice";
import { APP_ROUTE_LOGIN, APP_ROUTE_LOGIN_MFA } from "../../../constants/RouteConstants";
import PreLoginContainer from "../../common/preLoginContainer";
import Form from "../../common/form";
import * as Validations from "../../common/form/validator";
import { TextInput } from "../../common/fields";
import Link from "../../common/link";
import { errorText, TOO_MANY_ATTEMPTS } from "../../../helpers/ErrorMessage";

// /emergency-sign-in: for SysRoot users when company sign-in does not work.
// Nothing links here, and search engines are asked not to list it. Every
// refusal reads the same; only the rate limit (F21) says how long to wait.
export const EmergencySignIn = ({ sessionSuccess, mfaChallengeReceived }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [requesting, setRequesting] = useState(false);
  const [failed, setFailed] = useState(null);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const submit = async (values) => {
    setRequesting(true);
    setFailed(null);
    try {
      const ajax = await AuthService.emergencySignIn({
        username: values.username,
        password: values.password,
      });
      const response = ajax.data?.response;
      if (response?.mfaToken) {
        mfaChallengeReceived({ ...response, emergency: true });
        navigate(APP_ROUTE_LOGIN_MFA);
      } else {
        sessionSuccess(ajax.data);
      }
    } catch (error) {
      const body = error?.response?.data;
      setFailed(body?.response === TOO_MANY_ATTEMPTS ? body : { response: "SIGN_IN_FAILED" });
    } finally {
      setRequesting(false);
    }
  };

  return (
    <PreLoginContainer>
      <div className="title text-center">
        {intl.formatMessage({ id: "emergency_sign_in_title" })}
        <div className="tag-line text-muted">
          {intl.formatMessage({ id: "emergency_sign_in_text" })}
        </div>
      </div>
      <Form
        fullWidthButtons
        requesting={requesting}
        submitBtn={{
          label: intl.formatMessage({ id: "sign_in" }),
          icon: "pi-arrow-right",
          onClick: submit,
        }}
      >
        <div className="flex flex-column gap-2 mt-4">
          <Field
            name="username"
            component={TextInput}
            type="text"
            icon="pi-user"
            placeholder={intl.formatMessage({ id: "username" })}
            required
            validate={Validations.composeValidators(Validations.required)}
          />
          <Field
            name="password"
            component={TextInput}
            type="password"
            placeholder={intl.formatMessage({ id: "password" })}
            required
            validate={Validations.composeValidators(Validations.required)}
          />
          {(failed && (
            <div className="error-message">
              {errorText(failed, intl, "sign_in_failed")}
            </div>
          )) ||
            null}
        </div>
      </Form>
      <div className="text-center mt-1">
        <Link to={APP_ROUTE_LOGIN}>{intl.formatMessage({ id: "back_to_login" })}</Link>
      </div>
    </PreLoginContainer>
  );
};

const mapDispatchToProps = (dispatch) => ({
  sessionSuccess: (data) => dispatch(sessionSuccess(data)),
  mfaChallengeReceived: (data) => dispatch(mfaChallengeReceived(data)),
});

export default connect(null, mapDispatchToProps)(EmergencySignIn);
